/**
 * Display-only time formatting with EXPLICIT timezone labels.
 *
 * Why: across agent surfaces a bare "15 Jun 09:45" is ambiguous on a
 * multi-country team — nobody can tell which clock it's in. These helpers
 * append an IANA short label (IST / CEST / EDT …) to every rendered instant,
 * and offer a dual-zone form (site TZ + viewer TZ) for field activity.
 *
 * Pure presentation: NO stored value changes, no network. Built on the
 * platform `Intl` API; relative wording reuses date-fns (already a dep).
 * Reuses `zoneAbbrev` from ./tz so the abbreviation logic lives in one place.
 */
import { formatDistanceToNow } from 'date-fns';
import { zoneAbbrev, zoneName } from './tz';
/** The viewer's browser timezone (IANA), e.g. "Asia/Kolkata". UTC on failure. */
export function getViewerTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    }
    catch {
        return 'UTC';
    }
}
function toDate(instant) {
    if (instant == null || instant === '')
        return null;
    const d = instant instanceof Date ? instant : new Date(instant);
    return Number.isNaN(d.getTime()) ? null : d;
}
/**
 * Wall-clock parts of `d` rendered in `tz`. Invalid tz → falls back to UTC
 * (with a console warning) so a bad timezone string never throws in render.
 */
function zoneParts(d, tz) {
    const opts = {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    };
    let useTz = tz;
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-GB', { timeZone: useTz, ...opts }).formatToParts(d);
    }
    catch {
        console.warn(`[timeDisplay] invalid timezone "${tz}" — falling back to UTC`);
        useTz = 'UTC';
        parts = new Intl.DateTimeFormat('en-GB', { timeZone: useTz, ...opts }).formatToParts(d);
    }
    const p = {};
    for (const part of parts)
        if (part.type !== 'literal')
            p[part.type] = part.value;
    return {
        tz: useTz,
        day: p.day ?? '',
        month: p.month ?? '',
        year: p.year ?? '',
        hour: p.hour ?? '',
        minute: p.minute ?? '',
    };
}
/** Time-only ("13:25") in `tz` plus its abbreviation ("13:25 IST"). */
function timeWithZone(d, tz) {
    const q = zoneParts(d, tz);
    return `${q.hour}:${q.minute} ${zoneAbbrev(q.tz, d.toISOString())}`;
}
/**
 * "15 Jun 09:45 IST" — an instant rendered in `tz` with a zone label.
 * - `showAbbrev: false` swaps the abbreviation for the full IANA name.
 * - `withYear: true` includes the year ("15 Jun 2026 09:45 IST").
 * Returns "—" for an empty/invalid instant.
 */
export function formatWithZone(instant, tz, options) {
    const d = toDate(instant);
    if (!d)
        return '—';
    const showAbbrev = options?.showAbbrev ?? true;
    const withYear = options?.withYear ?? false;
    const q = zoneParts(d, tz);
    const date = withYear ? `${q.day} ${q.month} ${q.year}` : `${q.day} ${q.month}`;
    const label = showAbbrev ? zoneAbbrev(q.tz, d.toISOString()) : q.tz;
    return `${date} ${q.hour}:${q.minute} ${label}`;
}
/**
 * "15 Jun 09:55 CEST · 13:25 IST" — the same instant in two zones, for field
 * activity where the on-site clock and the viewer's clock both matter.
 * Primary is shown with the date; secondary is time-only.
 */
export function formatDualZone(instant, primaryTz, secondaryTz, options) {
    const d = toDate(instant);
    if (!d)
        return '—';
    const pl = options?.primaryLabel ? `${options.primaryLabel}: ` : '';
    const sl = options?.secondaryLabel ? `${options.secondaryLabel}: ` : '';
    return `${pl}${formatWithZone(d, primaryTz)} · ${sl}${timeWithZone(d, secondaryTz)}`;
}
/**
 * Relative wording for list cells, with a precise tooltip:
 * { display: "2 hours ago", tooltip: "15 Jun 09:45 IST · 04:15 UTC" }.
 */
export function formatRelative(instant, tz) {
    const d = toDate(instant);
    if (!d)
        return { display: '—', tooltip: '' };
    return {
        display: formatDistanceToNow(d, { addSuffix: true }),
        // Spell out the full zone name on hover so a label like "IST" is unambiguous.
        tooltip: `${zoneName(tz, d.toISOString())} — ${formatWithZone(d, tz)} · ${timeWithZone(d, 'UTC')}`,
    };
}
/**
 * Format a bare wall-clock string ("10:30", "9:05", "13:25:00") as 12-hour
 * with a meridiem ("10:30 AM"). For custom-field time values that are plain
 * strings with no date or zone (e.g. the field-service "Time of visit"), so a
 * 24-hour "10:30" is no longer mistaken for the evening. Anything that isn't a
 * clean 24-hour time is returned unchanged.
 */
export function formatClock(value) {
    const m = /^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(value.trim());
    if (!m)
        return value;
    const h24 = Number(m[1]);
    const meridiem = h24 < 12 ? 'AM' : 'PM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${h12}:${m[2]} ${meridiem}`;
}
/** Map an hour-of-day (0-23) to a one-word operational hint. */
function hourHint(hour) {
    if (hour < 6)
        return 'after hours';
    if (hour < 9)
        return 'early morning';
    if (hour < 12)
        return 'morning';
    if (hour < 14)
        return 'lunchtime';
    if (hour < 18)
        return 'afternoon';
    if (hour < 22)
        return 'evening';
    return 'late evening';
}
/**
 * For the ticket header: the create time rendered IN the requester's timezone
 * plus a contextual hint, so an agent reads the operational signal —
 * { time: "06:15 CEST", hint: "early morning" }. A 09:00 requester-local
 * raise reads "person just got in"; 23:30 reads "after-hours / working late".
 */
export function formatRequesterContext(createdAt, requesterTz) {
    const d = toDate(createdAt);
    if (!d)
        return { time: '—', hint: '' };
    const q = zoneParts(d, requesterTz);
    return {
        time: `${q.hour}:${q.minute} ${zoneAbbrev(q.tz, d.toISOString())}`,
        hint: hourHint(Number(q.hour)),
    };
}
